import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db } from "@/lib/firebase/config";
import { COLLECTIONS } from "@/lib/firebase/firestore";
import type { Booking, BookingStatus } from "@/types/booking";
import type { MealPlan } from "@/types/rooms";

// ─── Fetch single booking by ID ───────────────────────────────────────────────

export async function getBookingById(id: string): Promise<Booking | null> {
  const snap = await getDoc(doc(db, COLLECTIONS.BOOKINGS, id));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() } as Booking;
}

// ─── Lookup booking via Cloud Function (email + bookingRef) ──────────────────
// Uses Cloud Function instead of direct query to keep Firestore rules tight.

export async function lookupBooking(
  email: string,
  bookingRef: string,
): Promise<Booking | null> {
  try {
    const functions = getFunctions(undefined, "asia-south1");
    const fn = httpsCallable<{ email: string; bookingRef: string }, Booking>(
      functions,
      "lookupBooking",
    );
    const { data } = await fn({ email, bookingRef });
    return data;
  } catch {
    return null;
  }
}

// ─── Admin: get all bookings ──────────────────────────────────────────────────

export async function getAllBookings(): Promise<Booking[]> {
  const q = query(
    collection(db, COLLECTIONS.BOOKINGS),
    orderBy("createdAt", "desc"),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Booking);
}

// ─── Admin: get bookings by status ───────────────────────────────────────────

export async function getBookingsByStatus(
  status: BookingStatus,
): Promise<Booking[]> {
  const q = query(
    collection(db, COLLECTIONS.BOOKINGS),
    where("status", "==", status),
    orderBy("createdAt", "desc"),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Booking);
}

// ─── Admin: get bookings by date range ───────────────────────────────────────

export async function getBookingsByDateRange(
  from: string,
  to: string,
): Promise<Booking[]> {
  const q = query(
    collection(db, COLLECTIONS.BOOKINGS),
    where("checkIn", ">=", from),
    where("checkIn", "<=", to),
    orderBy("checkIn", "asc"),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Booking);
}

// ─── Admin: update booking status ────────────────────────────────────────────

export async function updateBookingStatus(
  id: string,
  status: BookingStatus,
  notes?: string,
): Promise<void> {
  await updateDoc(doc(db, COLLECTIONS.BOOKINGS, id), {
    status,
    ...(notes !== undefined && { notes }),
    updatedAt: serverTimestamp(),
  });
}

// ─── Admin: add internal note ─────────────────────────────────────────────────

export async function addBookingNote(id: string, notes: string): Promise<void> {
  await updateDoc(doc(db, COLLECTIONS.BOOKINGS, id), {
    notes,
    updatedAt: serverTimestamp(),
  });
}

// ─── Walk-in booking ──────────────────────────────────────────────────────────

export interface WalkInBookingData {
  roomTypeId: string;
  roomTypeName: string;
  roomId: string;
  mealPlan: MealPlan;
  checkIn: string;
  checkOut: string;
  nights: number;
  adults: number;
  children: number;
  guest: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    address?: string;
    specialRequests?: string;
  };
  pricing: {
    basePrice: number;
    nights: number;
    subtotal: number;
    gstRate: number;
    gstAmount: number;
    total: number;
    mealPlan: MealPlan;
  };
  notes?: string;
}

export async function createWalkInBooking(
  data: WalkInBookingData,
): Promise<string> {
  const bookingRef = generateBookingRef();
  const ref = await addDoc(collection(db, COLLECTIONS.BOOKINGS), {
    ...data,
    guests: { adults: data.adults, children: data.children },
    bookingRef,
    status: "confirmed",
    razorpayOrderId: null,
    razorpayPaymentId: null,
    guestUid: null,
    notes: data.notes ?? "",
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}

// ─── Guest: request cancellation ─────────────────────────────────────────────

export async function requestCancellation(
  bookingId: string,
  reason: string,
): Promise<void> {
  await updateDoc(doc(db, COLLECTIONS.BOOKINGS, bookingId), {
    status: "cancelled" as BookingStatus,
    cancellationReason: reason,
    cancelledAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

// ─── CSV export ───────────────────────────────────────────────────────────────

export function exportBookingsToCSV(bookings: Booking[]): void {
  const headers = [
    "Booking Ref",
    "Status",
    "Room Type",
    "Meal Plan",
    "Check In",
    "Check Out",
    "Nights",
    "Adults",
    "Children",
    "Guest Name",
    "Guest Email",
    "Guest Phone",
    "Base Price",
    "GST Rate",
    "GST",
    "Total",
  ];

  const rows = bookings.map((b) => [
    b.bookingRef,
    b.status,
    b.roomTypeName,
    b.mealPlan ?? "—",
    b.checkIn,
    b.checkOut,
    b.nights,
    b.guests.adults,
    b.guests.children,
    `${b.guest.firstName} ${b.guest.lastName}`,
    b.guest.email,
    b.guest.phone,
    b.pricing.subtotal,
    `${b.pricing.gstRate}%`,
    b.pricing.gstAmount,
    b.pricing.total,
  ]);

  const csv = [headers, ...rows]
    .map((row) => row.map((v) => `"${v}"`).join(","))
    .join("\n");

  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `bookings-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function generateBookingRef(): string {
  const date = new Date();
  const datePart = date.toISOString().slice(0, 10).replace(/-/g, "");
  const rand = Math.random().toString(36).slice(2, 6).toUpperCase();
  return `HTL-${datePart}-${rand}`;
}